import React, { memo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useRankNavigationItem } from '@atlassian/jira-horizontal-nav-rank-item-service/src/services/useRankNavigationItem.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { MoveItem_container$key } from './__generated__/MoveItem_container.graphql';
import type { MoveItem_tab$key } from './__generated__/MoveItem_tab.graphql';
import messages from './messages.tsx';

export const PACKAGE_NAME = 'jiraNavigationKitRankItem';
export const REORDER_VIEW_EXPERIENCE = { experience: 'reorderView' };

export type MoveItemProps = {
	beforeItemId?: string | null;
	afterItemId?: string | null;
	position: number;
	id: string;
	dataId: string;
	hasSeparator: boolean;
	onClick: () => void;
	isOverflowing?: boolean;
	tabFragment: MoveItem_tab$key;
	containerFragment: MoveItem_container$key;
};

export const MoveItem = memo(
	({
		id,
		dataId,
		beforeItemId,
		afterItemId,
		position,
		hasSeparator,
		onClick,
		isOverflowing,
		tabFragment,
		containerFragment,
	}: MoveItemProps): React.JSX.Element | null => {
		const { formatMessage } = useIntl();
		const [rankNavigationItem] = useRankNavigationItem();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const onStartExperience = useExperienceStart({
			...REORDER_VIEW_EXPERIENCE,
			analyticsSource: PACKAGE_NAME,
		});

		const tabData = useFragment(
			graphql`
				fragment MoveItem_tab on JiraNavigationItem {
					typeKey @required(action: THROW)
					... on JiraAppNavigationItem {
						appId
					}
				}
			`,
			tabFragment,
		);

		const containerData = useFragment(
			graphql`
				fragment MoveItem_container on JiraContainerNavigationResult {
					... on JiraContainerNavigation {
						id
						scopeId
					}
				}
			`,
			containerFragment,
		);

		const handleMoveAfter = useCallback(() => {
			if (
				!containerData.id ||
				!containerData.scopeId ||
				id.includes('optimisticId') ||
				(afterItemId && afterItemId.includes('optimisticId'))
			) {
				return;
			}

			onClick();
			onStartExperience();

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'projectNavigationTab', {
				action: 'moveTabAfter',
				itemType: tabData.typeKey.toLowerCase(),
				itemId: tabData.appId ?? undefined,
				isInMore: Boolean(isOverflowing),
			});

			rankNavigationItem({
				placement: 'after',
				sourceTabIndex: position,
				targetTabIndex: position + 1,
				id,
				afterItemId,
				dataId,
				actionType: 'clicked',
				containerNavigationId: containerData.id,
				scopeId: containerData.scopeId,
			});
		}, [
			containerData.id,
			containerData.scopeId,
			id,
			afterItemId,
			onClick,
			onStartExperience,
			createAnalyticsEvent,
			tabData.typeKey,
			tabData.appId,
			isOverflowing,
			rankNavigationItem,
			position,
			dataId,
		]);

		const handleMoveBefore = useCallback(() => {
			if (
				!containerData.id ||
				!containerData.scopeId ||
				id.includes('optimisticId') ||
				(beforeItemId && beforeItemId.includes('optimisticId'))
			) {
				return;
			}

			onClick();
			onStartExperience();

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'projectNavigationTab', {
				action: 'moveTabBefore',
				itemType: tabData.typeKey.toLowerCase(),
				itemId: tabData.appId ?? undefined,
				isInMore: Boolean(isOverflowing),
			});

			rankNavigationItem({
				placement: 'before',
				sourceTabIndex: position,
				targetTabIndex: position - 1,
				id,
				beforeItemId,
				dataId,
				actionType: 'clicked',
				containerNavigationId: containerData.id,
				scopeId: containerData.scopeId,
			});
		}, [
			containerData.id,
			containerData.scopeId,
			id,
			beforeItemId,
			onClick,
			onStartExperience,
			createAnalyticsEvent,
			tabData.typeKey,
			tabData.appId,
			isOverflowing,
			rankNavigationItem,
			position,
			dataId,
		]);

		if (!beforeItemId && !afterItemId) return null;

		return (
			<DropdownItemGroup hasSeparator={hasSeparator}>
				{beforeItemId && (
					<DropdownItem onClick={handleMoveBefore}>
						{formatMessage(isOverflowing ? messages.moveItemUp : messages.moveItemLeft)}
					</DropdownItem>
				)}
				{afterItemId && (
					<DropdownItem onClick={handleMoveAfter}>
						{formatMessage(isOverflowing ? messages.moveItemDown : messages.moveItemRight)}
					</DropdownItem>
				)}
			</DropdownItemGroup>
		);
	},
);
